import { ChevronRight, ExternalLink } from "lucide-react";
import { cn } from "@lab/src/lib/util";
import { ArchiveJobGroup, ArchiveFileType } from "@/lib/supabase/supabaseTypes";
import { Checkbox } from "../../../components/ui/checkbox";
import { Button } from "../../../components/ui/button";
import { Skeleton } from "../../../components/ui/skeleton";
import { ArchiveFileRow } from "./archive-file-row.component";
import { formatArchiveDate } from "../archive.utils";

interface ArchiveJobGroupProps {
	group: ArchiveJobGroup;
	isExpanded: boolean;
	isLoading: boolean;
	hasMore: boolean;
	files: ArchiveFileType[] | undefined;
	selectedIds: Set<string>;
	onToggleExpand: (group: ArchiveJobGroup) => void;
	onToggleFile: (fileId: string) => void;
	onSelectGroup: (fileIds: string[], selected: boolean) => void;
	onPreviewFile: (file: ArchiveFileType, siblingIds: string[]) => void;
	onDownloadFile: (file: ArchiveFileType) => void;
	onOpenJob: (jobId: number) => void;
	onLoadMoreFiles: (group: ArchiveJobGroup) => void;
}

export const ArchiveJobGroupRow: React.FC<ArchiveJobGroupProps> = ({
	group,
	isExpanded,
	isLoading,
	hasMore,
	files,
	selectedIds,
	onToggleExpand,
	onToggleFile,
	onSelectGroup,
	onPreviewFile,
	onDownloadFile,
	onOpenJob,
	onLoadMoreFiles,
}) => {
	const isNoJob = group.resolved_job_id === null;
	const title = isNoJob
		? "Ohne Auftrag"
		: group.job_code || group.job_title || "Auftrag";
	let subtitle: string | undefined;
	if (isNoJob) {
		subtitle = "Sammelrechnungen, Akonto, Uploads";
	} else if (group.job_title && group.job_code) {
		subtitle = group.job_title;
	}

	const loadedIds = (files?.map((file) => file.id).filter(Boolean) ??
		[]) as string[];
	const allSelected =
		loadedIds.length > 0 && loadedIds.every((id) => selectedIds.has(id));

	return (
		<div>
			<div className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50">
				<button
					type="button"
					className="flex min-w-0 flex-1 items-center gap-2 text-left"
					onClick={() => onToggleExpand(group)}
					aria-expanded={isExpanded}
					aria-label={`Auftrag ${title} ${
						isExpanded ? "zuklappen" : "aufklappen"
					}`}
					data-testid="archive-job-group-header"
				>
					<ChevronRight
						size={16}
						className={cn(
							"shrink-0 text-gray-400 transition-transform",
							isExpanded && "rotate-90"
						)}
					/>
					<span className="min-w-0 truncate font-medium">
						{title}
					</span>
					{subtitle && (
						<span className="min-w-0 truncate text-sm text-gray-500">
							{subtitle}
						</span>
					)}
				</button>
				{group.resolved_job_id !== null && (
					<Button
						variant="ghost"
						size="icon"
						className="h-7 w-7 shrink-0 text-muted-foreground"
						onClick={() =>
							onOpenJob(group.resolved_job_id as number)
						}
						aria-label={`Zum Auftrag ${title} wechseln`}
						data-testid="archive-open-job-button"
					>
						<ExternalLink size={13} />
					</Button>
				)}
				<span className="shrink-0 text-sm text-gray-400">
					{group.file_count.toLocaleString("de-DE")}{" "}
					{group.file_count === 1 ? "Datei" : "Dateien"}
				</span>
				<span className="w-24 shrink-0 text-right text-sm text-gray-400">
					{formatArchiveDate(group.latest_created_at)}
				</span>
			</div>

			{isExpanded && (
				<div className="border-t border-gray-100 bg-gray-50/50 pl-6">
					{isLoading && !files ? (
						<div className="flex flex-col gap-1 px-3 py-2">
							{Array.from({ length: 3 }).map((_, index) => (
								<Skeleton key={index} className="h-8 w-full" />
							))}
						</div>
					) : (
						<>
							{loadedIds.length > 1 && (
								<label className="flex items-center gap-3 border-b border-gray-100 px-3 py-1.5 text-xs text-gray-500">
									<Checkbox
										checked={allSelected}
										onCheckedChange={(value) =>
											onSelectGroup(loadedIds, !!value)
										}
										aria-label="Alle geladenen Dateien dieses Auftrags auswählen"
										data-testid="archive-group-select-loaded"
									/>
									Geladene auswählen
								</label>
							)}
							<div className="divide-y divide-gray-100">
								{files?.map((file) => (
									<ArchiveFileRow
										key={file.id}
										file={file}
										selected={selectedIds.has(
											file.id ?? ""
										)}
										onToggleSelect={onToggleFile}
										onPreview={(f) =>
											onPreviewFile(f, loadedIds)
										}
										onDownload={onDownloadFile}
									/>
								))}
							</div>
							{hasMore && (
								<div className="border-t border-gray-100 px-3 py-1">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => onLoadMoreFiles(group)}
										disabled={isLoading}
										aria-label="Weitere Dateien dieses Auftrags laden"
										data-testid="archive-group-load-more"
									>
										{isLoading
											? "Wird geladen…"
											: "Weitere Dateien laden"}
									</Button>
								</div>
							)}
						</>
					)}
				</div>
			)}
		</div>
	);
};
